/**
 * VERI*FACTU: ¿está habilitado y activo? Lo pregunta `GET /verifactu/config` y lo comparten todas
 * las pantallas. La respuesta caduca a los 5 minutos y se vuelve a pedir al recuperar el foco: una
 * pestaña abierta antes de la medianoche de la activación no puede seguir creyendo que no está activo.
 *
 * Sin activación (`enabled = false`) la web se comporta exactamente como antes: el selector del
 * código R de la AEAT, el tratamiento fiscal de las líneas y los bloques de registro solo aparecen
 * con `active = true`.
 */

import { useEffect, useState } from 'react'
import type { TaxTreatment, VerifactuConfigDto } from '@toga/shared'
import { api } from './api'

const TTL_MS = 5 * 60 * 1000

let cached: Promise<VerifactuConfigDto | null> | null = null
let loadedAt = 0
const listeners = new Set<(value: VerifactuConfigDto | null) => void>()

function load(force = false): Promise<VerifactuConfigDto | null> {
  if (cached === null || force || Date.now() - loadedAt > TTL_MS) {
    loadedAt = Date.now()
    const pending = api.get<VerifactuConfigDto>('/verifactu/config').catch(() => {
      cached = null // se reintenta en la próxima pantalla
      return null
    })
    cached = pending
    void pending.then((value) => {
      if (cached === pending) listeners.forEach((listener) => listener(value))
    })
  }
  return cached
}

/** Vuelve a pedir la configuración ya. */
export function refreshVerifactuConfig(): Promise<VerifactuConfigDto | null> {
  return load(true)
}

export function useVerifactuConfig(): VerifactuConfigDto | null {
  const [config, setConfig] = useState<VerifactuConfigDto | null>(null)
  useEffect(() => {
    let alive = true
    const update = (value: VerifactuConfigDto | null) => {
      if (alive) setConfig(value)
    }
    listeners.add(update)
    void load().then(update)
    const onFocus = () => {
      if (document.visibilityState !== 'hidden') void load()
    }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onFocus)
    return () => {
      alive = false
      listeners.delete(update)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onFocus)
    }
  }, [])
  return config
}

/** Tratamientos de una línea al 0 % (el S1 es el de siempre, con tipo > 0). */
export const ZERO_RATE_TREATMENTS: readonly TaxTreatment[] = [
  'S2',
  'N1',
  'N2',
  'E1',
  'E2',
  'E3',
  'E4',
  'E5',
  'E6',
]

/** Estado de un registro en la AEAT, en castellano. */
export const RECORD_STATUS_LABEL: Record<string, string> = {
  pending: 'Pendiente de enviar',
  in_flight: 'Enviándose',
  accepted: 'Aceptado por la AEAT',
  accepted_with_errors: 'Aceptado con errores',
  rejected: 'Rechazado',
  duplicate: 'Duplicado',
}
